// What this package needs from whichever app is hosting it. Set once, at boot,
// before anything else here runs.
//
// Only values that genuinely differ per surface live here. Everything read from
// the environment comes from this package's own `runtime-env`, which both apps
// now share — one ADR-0033 allow-list instead of two.

/** Which surface the hosting app is. */
export type HostAppContext = "web" | "admin";

/** Cognito group vocabulary, mirrored per surface. */
export type CognitoGroup = "customer" | "admin";

export interface WebCommonHost {
  /** The surface this bundle is. Compile-time constant in the app. */
  appContext: HostAppContext;
  /** Group this surface admits, and the one it refuses. */
  requiredGroup: CognitoGroup;
  oppositeGroup: CognitoGroup;
  /** Copy shown when an account belongs to the OTHER surface. */
  wrongSurfaceMessage: string;
  /**
   * Where `/register` points under `AUTH_MODE=oidc` when no https sign-up door
   * is configured. `none` hides Register (the customer surface: the app-local
   * Amplify form signs up against the wrong pool). `route` keeps the in-app
   * form. Retained only to keep this extraction behaviour-neutral — it goes
   * when the internal self-registration form does.
   */
  registerFallback: "none" | "route";
}

/**
 * Held on `globalThis`, not in a module variable. The config is set once per
 * page, but a module-scope copy is lost to anything that rebuilds the module
 * graph — `vi.resetModules()` in a test, or an HMR update — and every reader
 * here would then throw or, worse, be swallowed by a caller that collapses
 * errors to null. Same footgun as a module-scope token holder.
 */
const HOST_KEY = "__pennWebCommonHost";

type HostGlobal = typeof globalThis & { [HOST_KEY]?: WebCommonHost };

export function initWebCommon(cfg: WebCommonHost): void {
  (globalThis as HostGlobal)[HOST_KEY] = cfg;
}

export function webCommonHost(): WebCommonHost {
  const host = (globalThis as HostGlobal)[HOST_KEY];
  if (!host) {
    throw new Error(
      "@penn/web-common used before initWebCommon() — call it in the app entrypoint, before the first import that reads config.",
    );
  }
  return host;
}
